import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { PieChart, TrendingUp, DollarSign } from "lucide-react";

export default function ProfitAnalysisPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/invoices?limit=500"),
      api.get("/expenses?limit=500"),
    ]).then(([inv, exp]) => {
      const invoices = inv.data.invoices || [];
      const expenses = exp.data.expenses || [];
      const totalRev = invoices.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
      const totalExp = expenses.reduce((s, e) => s + (parseFloat(e.amount || e.total) || 0), 0);
      setData({ totalRev, totalExp, profit: totalRev - totalExp, margin: totalRev > 0 ? ((totalRev - totalExp) / totalRev * 100) : 0, invoiceCount: invoices.length, expCount: expenses.length });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="animate-pulse h-60 bg-gray-100 rounded-sm" />;
  if (!data) return null;
  const fmt = v => `R${(v || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;
  return (
    <div data-testid="profit-analysis-page">
      <div className="mb-6"><h1 className="font-heading font-bold text-2xl text-[#1F2937]">Profit Analysis</h1></div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="border rounded-sm"><CardContent className="p-5"><div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2 text-green-600 bg-green-50"><TrendingUp className="w-4 h-4" /></div><div className="text-xs text-gray-500 uppercase font-semibold">Total Revenue</div><div className="text-2xl font-bold text-[#1F2937] mt-1">{fmt(data.totalRev)}</div><p className="text-xs text-gray-400">{data.invoiceCount} invoices</p></CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-5"><div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2 text-red-600 bg-red-50"><DollarSign className="w-4 h-4" /></div><div className="text-xs text-gray-500 uppercase font-semibold">Total Expenses</div><div className="text-2xl font-bold text-[#1F2937] mt-1">{fmt(data.totalExp)}</div><p className="text-xs text-gray-400">{data.expCount} expenses</p></CardContent></Card>
        <Card className={`border rounded-sm ${data.profit >= 0 ? "border-green-200" : "border-red-200"}`}><CardContent className="p-5"><div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${data.profit >= 0 ? "text-green-600 bg-green-50" : "text-red-600 bg-red-50"}`}><PieChart className="w-4 h-4" /></div><div className="text-xs text-gray-500 uppercase font-semibold">Net Profit</div><div className={`text-2xl font-bold mt-1 ${data.profit >= 0 ? "text-green-700" : "text-red-700"}`}>{fmt(data.profit)}</div><p className="text-xs text-gray-400">Margin: {data.margin.toFixed(1)}%</p></CardContent></Card>
      </div>
    </div>
  );
}
